/**
 * Single source of truth for LaunchDarkly feature flags.
 *
 * Each entry maps an LD flag key to a code-friendly variable name and its default value.
 * To add a new flag, just add one entry here — everything else is derived automatically.
 *
 * @example
 *   { ldKey: 'my-new-feature', variableName: 'myNewFeatureFlag', defaultValue: false }
 */
// TODO: Revisit to correct flag names to match what we have in LD.
export const FLAG_DEFINITIONS = [
    { ldKey: 'enable-stocking-grade', variableName: 'enableStockingGrade', defaultValue: false },
    { ldKey: 'stockwave-global-search-dealer', variableName: 'stockwaveGlobalSearchDealerFlag', defaultValue: false },
    { ldKey: 'ag-modernization-test', variableName: 'agModernizationTestFlag', defaultValue: false },
    { ldKey: 'use-support-business-card', variableName: 'useSupportBusinessCardFlag', defaultValue: false },
    { ldKey: 'profit-time-extjs-theme', variableName: 'profitTimeExtjsTheme', defaultValue: false },
    {
        ldKey: 'profit-time-new-experience-enabled',
        variableName: 'profitTimeNewExperienceEnabled',
        defaultValue: false,
    },
    {
        ldKey: 'oem_package_equipment_competitive_set',
        variableName: 'oemPackageEquipmentCompetitiveSet',
        defaultValue: false,
    },
    { ldKey: 'stockwave-manheim-sso', variableName: 'stockwaveManheimSso', defaultValue: false },
    { ldKey: 'stockwave-mbondemand-library', variableName: 'mbNewDomainFlag', defaultValue: false },
    { ldKey: 'vauto-enable-pendo', variableName: 'vautoEnablePendo', defaultValue: false },
    { ldKey: 'stockwave-openlane-disable-pendo', variableName: 'stockwaveOpenlaneDisablePendo', defaultValue: false },
    { ldKey: 'ag-library-timings', variableName: 'agLibraryTimings', defaultValue: false },
    { ldKey: 'E55032-Global-Search-Stockwave-Financials-Integration', variableName: 'enableGlobalSearchFinancials', defaultValue: false },
    { ldKey: 'stockwave-glance-strategy-action', variableName: 'stockwaveGlanceStrategyActionFlag', defaultValue: true },
] as const;

/** Derived: variable name → default value (used by launchdarkly-flags.ts to seed the window store). */
export const FLAG_DEFAULTS = FLAG_DEFINITIONS.reduce(
    (acc, { variableName, defaultValue }) => {
        (acc as Record<string, unknown>)[variableName] = defaultValue;
        return acc;
    },
    {} as {
        readonly [K in (typeof FLAG_DEFINITIONS)[number]['variableName']]: (typeof FLAG_DEFINITIONS)[number]['defaultValue'];
    }
);

/** Derived: LD key → variable name (used by feature-flags.ts to sync only known flags). */
export const FLAG_KEY_ALIASES: Record<string, keyof typeof FLAG_DEFAULTS> = FLAG_DEFINITIONS.reduce(
    (acc, { ldKey, variableName }) => {
        acc[ldKey] = variableName;
        return acc;
    },
    {} as Record<string, keyof typeof FLAG_DEFAULTS>
);

export type LDFlags = typeof FLAG_DEFAULTS;
export type LDFlagName = keyof LDFlags;
export type LDFlagValue<K extends LDFlagName> = LDFlags[K];
export type LDFlagObserver<K extends LDFlagName> = (
    newValue: LDFlagValue<K>,
    oldValue: LDFlagValue<K>,
    flagName: K
) => void;
